/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { Box, CircularProgress } from "@mui/material";
import React, { useEffect, useState, ReactElement, useMemo, useCallback } from "react";
import axios from "axios";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import moment from "moment";
import UserTableSearch from "./UserTableSearch";
import SweetAlert2 from "react-sweetalert2";
import { Link } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineFileSearch } from "react-icons/ai";
import { MdOutlineDoNotDisturbAlt } from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";
import UserUpdateStatusConfirmModal from "../../shared/UserUpdateStatusConfirmModal";
import UserToolbar from "./UserToolbar";
import AgGridPagination from "../../shared/AgGridPagination";

ModuleRegistry.registerModules([AllCommunityModule]);

type User = {
  user_id: string;
  username: string;
  email: string;
  role: string;
  status: string;
  merchant: any;
  subaccount: any;
  created_date: string;
  latest_login: string;
};

const UserTable: React.FC<{
  isLoading: boolean;
  tableData: any;
  setRowCount: React.Dispatch<React.SetStateAction<number>>;
  setTableData: React.Dispatch<React.SetStateAction<any>>;
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentUsername: string;
  currentUserRole: any;
  handleSearch: any;
  handleClear: any;
  initialFilters: any;
  filters: any;
  setFilters: any;
  setIsLoading: any;
  fetchUsers: any;
  triggerReloadUserList: () => void;
}> = ({
  isLoading,
  tableData,
  setTableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  setRowCount,
  currentUsername,
  currentUserRole,
  handleSearch,
  handleClear,
  initialFilters,
  filters,
  setFilters,
  setIsLoading,
  fetchUsers,
  triggerReloadUserList
}) => {
    const [agents, setAgents] = useState([]);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const toggleFilters = () => setShowFilters((prev: boolean) => !prev);

    const userRole = localStorage.getItem("userRole")?.toLowerCase();
    const userid = localStorage.getItem("userid")?.toLowerCase();

    const [isOpenUpdateStatusConfirmModal, setIsOpenUpdateStatusConfirmModal] =
      useState<boolean>(false);
    const [statusToUpdate, setStatusToUpdate] = useState<string>("");
    const [userDetailToUpdate, setUserDetailToUpdate] = useState(null);

    useEffect(() => {
      const fetchData = async () => {
        const fetchedAgents = await getAgentOptions();
        setAgents(fetchedAgents);
      };

      fetchData();
    }, []);

    const getAgentOptions = async () => {
      localStorage.removeItem("agentName");
      try {
        if (currentUserRole === "SuperAdmin") {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/datasource/agents`
          );
          if (response.data.agents && response.data.agents.payload) {
            return [
              { id: "", name: "-- Please Select --" },
              ...response.data.agents.payload,
            ];
          } else {
            console.error("Invalid response format for get-all-agents");
            return [{ id: "", name: "-- Please Select --" }];
          }
        } else {
          return [];
        }
      } catch (error) {
        console.error(error);
        return [];
      }
    };

    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    useEffect(() => {
      const storedSelectedUser = localStorage.getItem("selectedUser");
      if (storedSelectedUser) {
        setSelectedUser(JSON.parse(storedSelectedUser));
        setEditingUser(true);
      }
    }, []);

    const [swalProps, setSwalProps] = useState<any>({});

    interface UserStatusItems {
      status: string;
      value: string;
      display: ReactElement;
    }

    const statusClasses = "status";

    const statusList: UserStatusItems[] = [
      {
        status: "Inactive",
        value: "inactive",
        display: (
          <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
        ),
      },
      {
        status: "Active",
        value: "active",
        display: (
          <Badge className={`${statusClasses} status-active`}>Active</Badge>
        ),
      },
    ];

    const formatDate = (dateString: string) => {
      return moment(dateString, "YYYY-MM-DD HH:mm:ss").format(
        "DD MMM YY - hh:mm A"
      );
    };

    const handleUpdateStatus = (statusToUpdate: string, user: any) => {
      setUserDetailToUpdate(user);
      setStatusToUpdate(statusToUpdate);
      setIsOpenUpdateStatusConfirmModal(true);
    };

    const closeUpdateStatusConfirmModal = () => {
      setIsOpenUpdateStatusConfirmModal(false);
    };

    const triggerReloadList = async () => {
      setUserDetailToUpdate(null);
      setStatusToUpdate("");
      setIsLoading(true);
      await fetchUsers(true);
    };

    const CustomLoadingOverlay = () => (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <CircularProgress size={40} />
      </div>
    );

    const EmailRenderer = (params: ICellRendererParams) => {
      const isCurrentUser = params.data.username === currentUsername;
      return (
        <span className={isCurrentUser ? "font-bold" : ""}>
          {params.data.email}
        </span>
      );
    };

    const UsernameRenderer = (params: ICellRendererParams) => {
      const isCurrentUser = params.data.username === currentUsername;
      return (
        <span className={isCurrentUser ? "font-bold" : ""}>
          {params.data.username}
        </span>
      );
    };

    const LatestLoginRenderer = (params: ICellRendererParams) => {
      const dateString = params.data.latest_login;
      if (!dateString) return <>-</>;
      return <>{formatDate(dateString)}</>;
    };

    const StatusRenderer = (params: ICellRendererParams) => {
      const status = params.data.status;
      const statusItem = statusList.find((item) => item.status === status);
      return <>{statusItem ? statusItem.display : null}</>;
    };

    const ActionsRenderer = (params: ICellRendererParams) => (
      <div className="flex items-center">
        <Tooltip title="View More Details" arrow placement="top">
          <div>
            <IconButton
              component={Link}
              to={`/${userRole}/user-management/${params.data?.user_id}`}
              disabled={isLoading}
              id={"button-detail-" + params.data?.user_id}
              onClick={() => {
                if (params.data?.agentId) {
                  const agent = agents.filter((x: any) => x.id === params.data.agentId)[0];
                  if (agent) localStorage.setItem("agentName", agent.name);
                }
              }}
              size="small"
            >
              <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
            </IconButton>
          </div>
        </Tooltip>
        {Number(userid) !== Number(params.data?.user_id) &&
          <Tooltip
            title={params.data.status === "Active" ? "Deactivate" : "Activate"}
            arrow
            placement="top"
          >
            <div>
              <IconButton
                disabled={isLoading}
                id={"button-change-status-" + params.data?.user_id}
                onClick={() =>
                  handleUpdateStatus(
                    params.data.status === "Active" ? "inactive" : "active",
                    params.data
                  )
                }
                size="small"
              >
                {params.data.status === "Active" ? (
                  <MdOutlineDoNotDisturbAlt className="h-5 w-5 text-red-500" />
                ) : (
                  <AiOutlineCheckCircle className="h-5 w-5 text-primary-500" />
                )}
              </IconButton>
            </div>
          </Tooltip>
        }
      </div>
    );

    const columns: ColDef[] = [
      {
        field: "email",
        headerName: "Email",
        sortable: false,
        minWidth: 140,
        flex: 1,
        cellRenderer: EmailRenderer,
      },
      {
        field: "username",
        headerName: "Username",
        sortable: false,
        minWidth: 140,
        flex: 1,
        cellRenderer: UsernameRenderer,
      },
      {
        field: "latest_login",
        headerName: "Latest Login",
        sortable: false,
        minWidth: 140,
        flex: 1,
        cellRenderer: LatestLoginRenderer,
      },
      {
        field: "status",
        headerName: "",
        sortable: false,
        width: 120,
        cellRenderer: StatusRenderer,
        cellClass: "ag-cell-status",
      },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        width: 100,
        resizable: false,
        cellRenderer: ActionsRenderer,
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
      },
    ];

    const defaultColDef = useMemo<ColDef>(
      () => ({
        resizable: true,
        suppressMenu: true,
      }),
      []
    );

    const hidePagination = rowCount <= pageSize;

    return (
      <div>
        {isOpenUpdateStatusConfirmModal && userDetailToUpdate && (
          <UserUpdateStatusConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenUpdateStatusConfirmModal}
            onClose={closeUpdateStatusConfirmModal}
            userToUpdate={userDetailToUpdate}
            statusToUpdate={statusToUpdate}
          />
        )}
        <SweetAlert2 {...swalProps} />

        <>
          <UserToolbar
            isLoading={isLoading}
            onToggleFilters={toggleFilters}
            onRefresh={triggerReloadUserList}
          />

          <UserTableSearch
            showFilters={showFilters}
            initialFilters={initialFilters}
            filters={filters}
            setFilters={setFilters}
            onSearch={handleSearch}
            onClear={handleClear}
            isLoading={isLoading}
            isConfirming={isConfirming}
          />

          <Box className="card w-full">
            <div style={{ width: "100%" }}>
              <AgGridReact
                theme={themeAlpine}
                rowData={isLoading || !agents ? [] : tableData}
                columnDefs={columns}
                defaultColDef={defaultColDef}
                getRowId={(params) => params.data.email}
                rowHeight={48}
                headerHeight={44}
                domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
                suppressPaginationPanel={true}
                loading={isLoading || !agents}
                loadingOverlayComponent={CustomLoadingOverlay}
                overlayNoRowsTemplate="No data available"
              />
            </div>

            <AgGridPagination
              page={page}
              pageSize={pageSize}
              rowCount={rowCount}
              onPaginationChange={setPaginationModel}
              hidden={hidePagination}
            />
          </Box>
        </>
      </div>
    );
  };

export default UserTable;
